<template>
  <v-container grid-list-xl>
    <v-layout wrap>
      <!-- HEADER -->
      <v-flex xs12 class="pb-0">
        <h1 class="headline mb-0 ml-1">
          <v-btn
            class="mr-4 mb-2"
            small
            text
            outlined
            color="primary"
            to="/tools/manage/list"
            style="margin-top:5px"
          >
            <v-icon small class="mr-1">chevron_left</v-icon>Tool List</v-btn
          >Tool Finder
        </h1>
      </v-flex>
      <v-flex xs12 sm12 md4>
        <v-card v-if="tool">
          <v-card-text>
            <v-img :src="tool.image" width="120" height="120" contain />
            <h1 class="title">{{ tool.title }}</h1>
            <p>{{ tool.description }}</p>
            <v-subheader class="pa-0 title font-weight-regular"
              >Summary</v-subheader
            >
            <v-container fluid class="pa-0">
              <v-row dense>
                <v-col cols="3">
                  {{ numberWithCommas(total) }}
                </v-col>

                <v-col cols="9">
                  TOTAL ON JOB SITES
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="3">
                  {{ toolLocations.length }}
                </v-col>

                <v-col cols="9">JOB SITES</v-col>
              </v-row>
              <v-row dense>
                <v-col cols="3">
                  ${{ numberWithCommas(tool.unitPrice || 0) }}
                </v-col>
                <v-col cols="9">
                  UNIT PRICE
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="3">
                  ${{
                    numberWithCommas((total * (tool.unitPrice || 0)).toFixed(2))
                  }}
                </v-col>
                <v-col cols="9">
                  TOTAL VALUE
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-flex>

      <!-- Tool list -->
      <v-flex xs12 sm12 md8>
        <v-progress-linear v-if="loading" indeterminate color="primary" />
        <v-card v-else-if="toolLocations.length">
          <v-data-table
            :headers="headers"
            :items="toolLocations"
            :items-per-page="12"
            :footer-props="{ 'items-per-page-options': [12, 24, 48, -1] }"
            item-key="jobSite.id"
            sort-by="totalQty"
            sort-desc
            dense
          >
            <template v-slot:item.jobSite="{ item }">
              <p
                class="ma-0 pa-0 body-2 font-weight-bold"
                style="line-height:1.4em"
              >
                {{ item.jobSite.street }}
                <span class="font-weight-regular">
                  {{ item.jobSite.street2 }}</span
                >
                <v-chip
                  v-if="item.jobSite.status !== 'open'"
                  x-small
                  label
                  class="ml-1"
                  >Job closed</v-chip
                >
              </p>

              <p class="caption grey--text ma-0 pa-0" style="line-height:1.4em">
                {{ item.jobSite.city }}
                {{ item.jobSite.postalCode }}
              </p>

              <p
                class="caption blue--text text--darken-4 ma-0 pa-0"
                style="line-height:1.4em"
              >
                Job ID:
                <strong>{{ item.jobSite.jobId }} </strong>
                <span
                  v-if="item.jobSite.department"
                  style="text-transform:uppercase"
                >
                  {{ item.jobSite.department.join(", ") }}</span
                >
              </p>
            </template>
            <template v-slot:item.latestOrderDate="{ item }">
              <template v-if="item.latestOrderDate">
                Order #{{ item.latestOrderNumber }}

                on
                {{ item.latestOrderDate.toDate() | moment("MMM Do YYYY") }}
                <br />
              </template>
              <v-btn
                outlined
                text
                x-small
                class="ml-2"
                color="primary"
                target="_blank"
                :to="`/jobs/orders/${item.jobSite.id}`"
                >Job Order Summary</v-btn
              >
            </template>
          </v-data-table>
        </v-card>
        <div v-else class="grey--text">{{ noToolsMessage }}</div>
      </v-flex>
    </v-layout>
  </v-container>
</template>
<script>
import fb from "@/main";
import { sum } from "lodash";
import utils from "@/helpers/utils";

// Consumables still tracked on site (same list as the Tools on Site page)
const TRACKED_CONSUMABLES = ["SW2dtoe08FW9fY3fXk2j", "nwwKZmf4En4YPqJMUiae"];
// Jobs used to test in production
const TEST_JOB_IDS = ["12-3456"];

export default {
  data() {
    return {
      loading: false,
      toolLocations: [],
      tool: null,
      total: 0,
      headers: [
        { text: "On Site", value: "totalQty", align: "left", width: 90 },
        {
          text: "",
          align: "left",
          value: "jobSite",
          sortable: false,
        },
        {
          text: "Recent order",
          align: "right",
          value: "latestOrderDate",
          sortable: false,
        },
      ],
      noToolsMessage: "",
      numberWithCommas: utils.numberWithCommas,
    };
  },
  async created() {
    this.findTool();
  },
  methods: {
    async findTool() {
      try {
        this.loading = true;
        const toolId = this.$route.params.toolId;
        const toolDoc = await fb.db.collection("tools").doc(toolId).get();
        if (!toolDoc.exists) {
          this.noToolsMessage = "Tool not found.";
          return;
        }
        this.tool = { id: toolDoc.id, ...toolDoc.data() };
        if (this.tool.consumable && !TRACKED_CONSUMABLES.includes(toolId)) {
          this.noToolsMessage = "Consumables aren't tracked on job sites.";
          return;
        }
        // Recounted on every tool order and pickup change
        // (functions/src/job-tools-on-site.ts)
        const resJobs = await fb.db
          .collection("jobToolsOnSite")
          .where("toolIds", "array-contains", toolId)
          .get();
        const toolLocations = await Promise.all(
          resJobs.docs.map(async (doc) => {
            const jobToolsOnSite = doc.data();
            const jobSiteDoc = await fb.db
              .collection("jobSites")
              .doc(doc.id)
              .get();
            return {
              ...jobToolsOnSite,
              totalQty: jobToolsOnSite.tools.find((t) => t.id === toolId).qty,
              jobSite: { id: doc.id, ...jobSiteDoc.data() },
            };
          })
        );
        this.toolLocations = toolLocations.filter(
          (l) => !TEST_JOB_IDS.includes(l.jobSite.jobId)
        );
        this.total = sum(this.toolLocations.map((t) => t.totalQty));
        if (!this.toolLocations.length) {
          this.noToolsMessage = `No ${this.tool.title} on any job site.`;
        }
      } catch (err) {
        console.log(err);
        alert("Error getting job data");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
